import React, { useEffect, useState, useRef } from "react";
import { Card, CardHeader, CardBody, ExpansionPanel, ExpansionPanelContent } from "@progress/kendo-react-layout";
import { Upload } from "@progress/kendo-react-upload";
import { Reveal } from "@progress/kendo-react-animation";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Label, Error } from "@progress/kendo-react-labels";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import Requests from "./Requests";
import Notification from "./Notification.tsx";
import { extensionTypes, specialCharacters } from "./components/data/constants";
import "./DocumentViewerStyles.scss";
import SkeletonDocumentViewer from "./components/Skeletons/SkeletonDocumentViewer";
import { Utils } from "./helpers";

// Add these utility functions at the top of your file, after the imports

const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  const suffixes = ['th', 'st', 'nd', 'rd'];
  return suffixes[day % 10] || 'th';
};

const formatCreationDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  const day = date.getDate();
  const ordinalSuffix = getOrdinalSuffix(day);
  const monthYear = date.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });
  
  return `${day}${ordinalSuffix} ${monthYear}`;
};

const CreationInfo = ({ selectedDocument, core, profileAdapter }) => {
  const handleProfileClick = (event) => {
    if (selectedDocument?.created_by) {
      profileAdapter.showProfileInfo(event, selectedDocument.created_by);
    }
  };

  const userProfileURL = React.useMemo(() => {
    if (!selectedDocument?.created_by || !core) return '';
    return `${core.config("wrapper.url")}user/profile/${selectedDocument.created_by}`;
  }, [selectedDocument?.created_by]);

  return (
    <div 
      className="created-date mt-2 mb-2 text-sm text-gray-400 font-light" 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        flexWrap: 'nowrap' 
      }}
    >
      <i className="fa-regular fa-calendar mr-1"></i>
      <span style={{ whiteSpace: 'nowrap' }}>
        Created: {formatCreationDate(selectedDocument?.date_created)}
      </span>
      
      <span style={{ marginLeft: '12px', whiteSpace: 'nowrap' }}>
        Created by:
      </span>
      
      {selectedDocument?.created_by && userProfileURL && (
        <img src={userProfileURL} className="user-img rounded-full cursor-pointer" onClick={handleProfileClick} style={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "20px",
          width: "20px",
          marginLeft: '4px',
          flexShrink: 0
        }} />
      )}
    </div>
  );
};

const DocumentViewer = (props) => {
  const { core, appId, url } = props;
  const alertAdapter = core?.make("oxzion/alert");
  const loader = core.make("oxzion/splash");
  const helper = core.make("oxzion/restClient");
  const baseUrl = core.config("wrapper.url").endsWith("/") ? core.config("wrapper.url") : core.config("wrapper.url") + "/";
  let urlRes = url.split("/");
  const fileId = urlRes[urlRes.length - 2];
  const notif = React.createRef();
  const [isLoading, setIsLoading] = useState(true);
  const [documentsList, setDocumentsList] = useState({});
  const [selectedFolderName, setSelectedFolderName] = useState("Attachments");
  const [selectedDocument, setSelectedDocument] = useState(undefined);
  const [entityConfig, setEntityConfig] = useState(props.entityConfig ?? null);
  const [selectedFieldKey, setSelectedFieldKey] = useState("attachments");
  const [fieldKeys, setfieldKeys] = useState({});
  const documentsResponse = useRef(null);

  // check if the EOX office is enabled (ox_app) from the database and change the messages accordingly
  const apps = core.make("oxzion/profile").get().key.apps;
  const hasEOXOfficeApp = apps.some((i) => i.name === "EOXOffice");

  // Default to false
  let hasStorageType = false;

  if (core.configuration?.storage?.type === "onlyoffice") hasStorageType = hasEOXOfficeApp;
  if (core.configuration?.storage?.type === "sharepoint") hasStorageType = true;

  useEffect(() => {
    getDocumentsList();
    if (!entityConfig) fetchEntityDetails();
    //cleanup
    return () => {
      setDocumentsList({});
    };
  }, []);

  const fetchEntityDetails = () => {
    if (props.fileData?.entity_id) {
      helper.request("v1", "/app/" + appId + "/entity/" + props.fileData?.entity_id + "/page", {}, "get").then((response) => {
        if (response.status === "success" && response.data) setEntityConfig(response.data);
      });
    }
  };

  const findFolderForDocument = (documentsList, document) => {
    for (const [folderName, documents] of Object.entries(documentsList)) {
      if (documents.some((doc) => doc.uuid === document.uuid)) {
        return folderName;
      }
    }
    return null;
  };

  const getDocumentsList = async (preferredDocument = null) => {
    try {
      setIsLoading(true);
      const response = await Requests.getDocumentsListService(core, appId, url);
      if (response.data) {
        documentsResponse.current = response.data;
        const newDocumentsList = {};
        const newFieldList = {};

        response.data.map((data) => {
          let docType = data.fieldName;
          newFieldList[data.fieldName] = data.fieldKey;
          newDocumentsList[docType] = data.attachments.length > 0 ? data.attachments : [];
        });
        setfieldKeys(newFieldList);
        let fieldLabel = Object.keys(newDocumentsList)[0];
        setDocumentsList(newDocumentsList);
        // Set the selected folder based on preferred document or default to first folder
        if (preferredDocument) {
          const documentFolder = findFolderForDocument(newDocumentsList, preferredDocument);
          if (documentFolder) {
            setSelectedFolderName(documentFolder);
            setSelectedFieldKey(newFieldList[documentFolder]);
          }
          setSelectedDocument(preferredDocument);
        } else {
          setSelectedFolderName(Object.keys(newDocumentsList)[0]);
          setSelectedFieldKey(newFieldList[fieldLabel]);
          if (newDocumentsList[Object.keys(newDocumentsList)[0]]?.[0]) {
            setSelectedDocument(newDocumentsList[Object.keys(newDocumentsList)[0]][0]);
          }
        }
      } else {
        notif?.current?.notify("", "No documents found!", "error", undefined, 3000);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      notif?.current?.notify("", "Failed to fetch documents. Please try again.", "error", undefined, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDocument = (document) => {
    if (selectedDocument?.uuid === document?.uuid) return;
    setSelectedDocument(document);
  };

  const handleDelete = async (document, folderName) => {
    setSelectedFolderName(folderName);
    const messageBody = `Are you sure to delete ${document.name} ?`;
    alertAdapter
      ?.show({
        title: "Confirm Delete",
        text: messageBody,
        confirmButtonText: "Yes, delete",
        cancelButtonText: "No, cancel",
      })
      .then((result) => {
        if (result) {
          loader.show();
          Requests.deleteFile(core, appId, fileId, document.uuid).then((response) => {
            if (response.status == "success") {
              let tempIndex = undefined;
              documentsList[selectedFolderName] = documentsList[selectedFolderName].filter((item, index, array) => {
                if (item.uuid === document.uuid) {
                  index === array.length - 1 ? (tempIndex = index - 1) : (tempIndex = index);
                }
                return item.uuid !== document.uuid;
              });
              setSelectedDocument(documentsList[selectedFolderName][tempIndex] || undefined);
              setDocumentsList(documentsList);
              loader.destroy();
              alertAdapter.show({ title: "Attachment deleted successfully!", confirmButtonText: "Got it", icon: "success" });
            } else {
              loader.destroy();
              alertAdapter.show({ title: response.message, confirmButtonText: "Got it", icon: "error" });
            }
          });
        }
      });
  };

  // Function to replace filename in path and url in the last part
  const replaceFileName = (str, newFileName) => {
    return str.replace(/[^/]+$/, newFileName);
  };

  const getMessage = (data) => {
    if (data.message) return data.message;
    if (typeof data.data === "string") return JSON.parse(data.data)?.message;
    return null;
  };

  const handleRename = async (newName, oldName, folderName) => {
    if (newName == oldName || newName.trim() == "") {
      return;
    }

    setSelectedFolderName(folderName);

    alertAdapter?.show({
      title: "Initiating rename...",
      icon: "info",
      allowOutsideClick: false,
    });

    const payload = {
      name: newName + "." + selectedDocument.extension,
    };

    Requests.renameFile(core, appId, fileId, selectedDocument.uuid, payload.name, oldName, selectedDocument.url, selectedDocument.path)
      .then((response) => {
        if (response.status === "success") {
          const updatedDocument = { ...selectedDocument, name: payload.name };
          setSelectedDocument(updatedDocument);
          setDocumentsList((prev) => {
            const updatedList = { ...prev };
            updatedList[folderName] = updatedList[folderName].map((doc) => (doc.uuid === selectedDocument.uuid ? updatedDocument : doc));
            return updatedList;
          });
          alertAdapter?.update({
            title: "File renamed successfully!",
            icon: "success",
            confirmButtonText: "Got it",
            allowOutsideClick: false,
          });
        } else {
          alertAdapter?.update({
            title: response.message || "Failed to rename file. Please try again.",
            icon: "error",
            confirmButtonText: "Got it",
            allowOutsideClick: false,
          });
        }
      })
      .catch((error) => {
        console.error("Error renaming file:", error);
        alertAdapter?.update({
          title: error.message || "Failed to rename file. Please try again.",
          icon: "error",
          confirmButtonText: "Got it",
          allowOutsideClick: false,
        });
      });
  };

  const handleShareLink = async (action, document = selectedDocument) => {
    if (core.configuration?.storage?.type === "sharepoint") {
      Utils.openShareLink({
        action,
        document,
        fileId,
        getMessage,
        appId,
      });
    } else if (core.configuration?.storage?.type === "onlyoffice")
      core.run("EOXOffice", { appId: appId, fileId: fileId, docData: document, mode: action });
  };

  if (isLoading) {
    return <SkeletonDocumentViewer />;
  }

  return (
    <>
      <Notification ref={notif} />
      <div className="row document-viewer-container">
        <div className="col-md-4">
          {Object.keys(documentsList).length > 0 && (
            <DocumentFoldersPanel
              key={"DocumentFoldersPanel"}
              core={core}
              fileId={fileId}
              helper={helper}
              appId={appId}
              notif={notif}
              loader={loader}
              alertAdapter={alertAdapter}
              documentsList={documentsList}
              getDocumentsList={getDocumentsList}
              selectedDocument={selectedDocument}
              setSelectedDocument={setSelectedDocument}
              selectedFolderName={selectedFolderName}
              setSelectedFolderName={setSelectedFolderName}
              onSelectDocument={handleSelectDocument}
              onDeleteDocument={handleDelete}
              entityConfig={entityConfig}
              handleShareLink={handleShareLink}
              fieldKeys={fieldKeys}
              setSelectedFieldKey={setSelectedFieldKey}
              selectedFieldKey={selectedFieldKey}
              hasStorageType={hasStorageType}
              documentsResponse={documentsResponse}
            />
          )}
        </div>
        <div className="col-md-8">
          {selectedDocument ? (
            <DocumentView
              key={"DocumentView"}
              core={core}
              notif={notif}
              selectedDocument={selectedDocument}
              setSelectedDocument={setSelectedDocument}
              selectedFolderName={selectedFolderName}
              onDeleteDocument={handleDelete}
              entityConfig={entityConfig}
              handleShareLink={handleShareLink}
              handleRename={handleRename}
              hasStorageType={hasStorageType}
            />
          ) : (
            <Card>
              <CardBody className="text-center">
                <div className="row mt-2 document-viewer-container">
                  <div className="col-md-12">
                    <div className="my-5 py-5">
                      <h4>No files found</h4>
                      <br />
                      {!entityConfig?.disable_create_upload && <p>You can upload files or create a new one using Office to get started.</p>}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

// /////////////////////////////////////////////////////////////////////
const DocumentFoldersPanel = ({
  core,
  fileId,
  helper,
  appId,
  notif,
  loader,
  alertAdapter,
  documentsList,
  getDocumentsList,
  selectedDocument,
  setSelectedDocument,
  selectedFolderName,
  setSelectedFolderName,
  onSelectDocument,
  onDeleteDocument,
  entityConfig,
  handleShareLink,
  fieldKeys,
  setSelectedFieldKey,
  selectedFieldKey,
  hasStorageType,
  documentsResponse,
}) => {
  const [visible, setVisible] = React.useState(false);
  const [selectedFiletype, setSelectedFiletype] = useState({ key: "Document", value: "docx" });
  const [fileName, setFileName] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileTypeOptions = [
    { key: "Document", value: "docx" },
    { key: "SpreadSheet", value: "xlsx" },
    { key: "Presentation", value: "pptx" },
  ];
  const uploadRefs = useRef({});
  const viewUrl = core.config("ui.url");

  // Used to set the selected folder name immediately
  useEffect(() => {
    // Only auto-select if no document is currently selected
    if (!selectedDocument && selectedFolderName && documentsList[selectedFolderName]?.length > 0) {
      const firstDoc = documentsList[selectedFolderName][0];
      if (firstDoc?.uuid) {
        onSelectDocument(firstDoc);
      }
    }
  }, [selectedFolderName, documentsList]);

  const handleCreate = async (fileName, type) => {
    if (!fileName) {
      notif?.current?.notify("", "Please enter a file name.", "error", undefined, 3000);
      return;
    }

    try {
      loader.show();
      const newDocument = `${fileName}.${type}`;
      const fetchedFile = await fetch(`${viewUrl}template/new.${type}`);
      const blob = await fetchedFile.blob();
      const templateFile = new File([blob], newDocument);
      const fileExists = documentsList[selectedFolderName]?.some((doc) => doc.name === newDocument);

      if (fileExists) {
        alertAdapter.show({
          title: "Failed to upload!",
          text: `File name ${newDocument} already exists.`,
          confirmButtonText: "Got it",
          icon: "error",
        });
        return;
      }

      const filesData = [
        {
          name: templateFile.name,
          type: `file/${type}`,
          fieldKey: selectedFieldKey,
        },
      ];

      const response = await postAttachments({ filesData, files: [templateFile] }, true);

      if (response?.status === "success") {
        const preferredDocument = response.data?.length > 0 ? response.data[0] : null;
        getDocumentsList(preferredDocument);
        if (preferredDocument) {
          handleShareLink("edit", preferredDocument);
        }
      } else {
        alertAdapter.show({ title: "File Creation failed.", confirmButtonText: "Got it", icon: "error" });
      }
    } catch (error) {
      alertAdapter.show({ title: "File Creation failed.", confirmButtonText: "Got it", icon: "error" });
    } finally {
      loader.destroy();
      toggleDialog();
      setFileName("");
    }
  };

  const postAttachments = async (allFileData) => {
    let response = await helper.request("v1", `/app/${appId}/file/${fileId}/attachment`, allFileData, "fileupload");
    return response;
  };

  const handleExpansion = (name) => {
    // Toggle behavior: if clicking the same folder, collapse it (set to null)
    const newSelectedFolder = selectedFolderName === name ? null : name;
    setSelectedFolderName(newSelectedFolder);
    setSelectedFieldKey(fieldKeys[name]);

    // Only select document if we're expanding (not collapsing)
    if (newSelectedFolder && documentsList[newSelectedFolder]?.[0]) {
      onSelectDocument(documentsList[newSelectedFolder][0]);
    }
  };

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const shortenFilename = (name) => {
    return name.substring(0, 12) + "..." + name.split(".")[1];
  };

  const CustomTitle = ({ name }) => {
    return (
      <div className="d-flex justify-content-between align-items-center w-100 p-0">
        <i className="fa fa-folder mr-2"></i>
        <span className="custom-title">{name}</span>
      </div>
    );
  };

  const convertFromEscape = (escapedString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(escapedString, "text/html");
    return doc.documentElement.textContent;
  };

  const onFileChange = (event) => {
    if (!event || !event.newState) {
      return;
    }
    const validFiles = event.newState.filter((item) => !item.validationErrors?.length);
    const fileError = event.newState.length !== validFiles.length;

    if (validFiles && validFiles.length > 0) {
      validFiles.forEach((each) => {
        each.name = convertFromEscape(each.name)
          ?.replace(specialCharacters, "")
          ?.replace(/[\[\\\]]/g, ""); // Simplified regex for [ \ ]
      });
      uploadAttachments(validFiles);
    }
    if (fileError) {
      console.log("🚀 ~ onFileChange ~ fileError:", fileError);
      alertAdapter.show({
        title: "Unsupported File",
        confirmButtonText: "Got it",
        icon: "error",
      });
    }
  };

  const uploadAttachments = async (files) => {
    loader.show();

    let fileList = documentsList[selectedFolderName]?.map((a) => a.name);
    let uniqueFiles = files.filter((file) => !fileList?.includes(file.name));
    let duplicateFiles = files.filter((file) => fileList?.includes(file.name));

    if (duplicateFiles.length > 0) {
      alertAdapter.show({
        title: "Skipping duplicate(s): ",
        text: `${duplicateFiles.map((f) => f.name).join(", ")}`,
        confirmButtonText: "Got it",
      });
    }
    if (uniqueFiles.length === 0) {
      loader.destroy();
      return;
    }

    let allFiles = [];
    let filesData = uniqueFiles.map((file) => {
      let rawFile = file.getRawFile();
      allFiles.push(rawFile);
      return {
        name: file.name,
        type: rawFile.type,
        fileId: fileId,
        fieldKey: selectedFieldKey,
      };
    });

    try {
      const response = await postAttachments({ filesData, files: allFiles });
      if (response?.status === "success") {
        setUploadedFiles([]);

        // Select the first uploaded file as the active document
        const preferredDocument = response.data?.length > 0 ? response.data[0] : null;
        getDocumentsList(preferredDocument);

        alertAdapter.show({ title: "Files Uploaded.", confirmButtonText: "Got it", icon: "success" });
      } else {
        alertAdapter.show({ title: response.message, confirmButtonText: "Got it", icon: "error" });
      }
    } catch (e) {
      alertAdapter.show({ title: "Error: " + (e?.message || e), confirmButtonText: "Got it", icon: "error" });
    } finally {
      loader.destroy();
    }
  };

  return (
    <Card className="border-none prevent-select">
      <CardBody className="card-50 p-0 pr-1">
        {Object.entries(documentsList)?.map(([name, contents]) => {
          return (
            !(entityConfig?.hide_empty_folders && contents.length === 0) && (
              <>
                <ExpansionPanel
                  key={name}
                  className="bg-teal mt-1 p-0 rounded"
                  title={<CustomTitle name={name} />}
                  expanded={selectedFolderName === name}
                  onAction={() => handleExpansion(name)}
                >
                  <Reveal>
                    {selectedFolderName === name && (
                      <ExpansionPanelContent className="bg-white">
                        <div className="content documentslisting">
                          <div className="my-2 doclist-container">
                            {contents?.length > 0 ? (
                              contents.map((attachment) => {
                                if (!attachment?.uuid) return null;
                                return (
                                  <Card
                                    onClick={() => {
                                      onSelectDocument(attachment);
                                    }}
                                    className="border-none"
                                    key={attachment.uuid}
                                  >
                                    <CardBody
                                      className={
                                        attachment?.uuid === selectedDocument?.uuid
                                          ? "pt-1 attachments-card-body cursor-pointer selected"
                                          : "pt-1 attachments-card-body cursor-pointer"
                                      }
                                    >
                                      <i className="fa fa-file fa-lg"></i>
                                      <span title={attachment.name} className="content-text ml-2">
                                        {attachment?.name?.length < 25 ? attachment.name : shortenFilename(attachment.name)}
                                      </span>
                                      {attachment?.uuid === selectedDocument?.uuid &&  !entityConfig?.disable_create_upload && (
                                        <i
                                          className="fa fa-xmark float-right"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteDocument(attachment, name);
                                          }}
                                          title={`Delete ${attachment.name}`}
                                        ></i>
                                      )}
                                    </CardBody>
                                  </Card>
                                );
                              })
                            ) : (
                              <Card className="border-none">
                                <CardBody className="attachments-card-body cursor-pointer">
                                  <span className="ml-2">No files found.</span>
                                  {hasStorageType && !entityConfig?.disable_create_upload && (
                                    <span className="ml-2">You can upload files or create a new one using Office to get started.</span>
                                  )}
                                </CardBody>
                              </Card>
                            )}
                          </div>
                        </div>
                        {!entityConfig?.disable_create_upload 
                          && (
                          <div className="justify-content-md-center mt-2 row">
                            <div className="col-md-6 text-center">
                              <Button
                                fillMode="solid"
                                themeColor="primary"
                                rounded="full"
                                onClick={() => {
                                  const uploadButton = uploadRefs.current[name]?.querySelector(".k-upload-button");
                                  if (uploadButton) {
                                    uploadButton.click();
                                  } else {
                                    console.error("Upload button not found. uploadRefs.current[name]:", uploadRefs.current[name], "for folder:", name);
                                  }
                                }}
                                title={`Upload a file`}
                              >
                                <i className="fa fa-plus mr-2"></i>Upload
                              </Button>

                              {/* Visibility set to hidden as Upload happens on button click */}
                              <div ref={(el) => uploadRefs.current[name] = el} style={{ position: "absolute", left: "-9999px" }}>
                                <Upload
                                  batch={true}
                                  multiple={true}
                                  autoUpload={false}
                                  files={uploadedFiles}
                                  onAdd={onFileChange}
                                  onRemove={onFileChange}
                                  restrictions={{
                                    allowedExtensions: extensionTypes,
                                    maxFileSize: 50000000,
                                  }}
                                  />
                              </div>
                            </div>
                            <div className="col-md-6 text-center">
                              <Button
                                fillMode={"clear"}
                                rounded="full"
                                onClick={toggleDialog}
                                title={`Create a new file in Office`}
                                className="clear-bordered"
                              >
                                <i className="fa fa-plus mr-2"></i>Create
                              </Button>
                            </div>
                          </div>
                        )}
                      </ExpansionPanelContent>
                    )}
                  </Reveal>
                </ExpansionPanel>

                {visible && (
                  <Dialog className="custom-dialog" onClose={toggleDialog}>
                    <Card className="">
                      <CardHeader>
                        <div className="row">
                          <div className="col-10">
                            <p className="mb-1">Create a new File in Office</p>
                          </div>
                          <div className="col-2">
                            <span className="card-close" onClick={toggleDialog}>
                              <i className="fa fa-close"></i>
                            </span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardBody>
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Label editorId="fileName" className="k-form-label" style={{ width: "30%", marginRight: "10px" }}>
                              File name
                            </Label>
                            <div className="k-form-field-wrap" style={{ width: "70%" }}>
                              <Input
                                type="text"
                                id="fileName"
                                value={fileName}
                                onChange={(event) => setFileName(event.target.value)}
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        </div>

                        <div style={{ marginBottom: 20 }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Label editorId="fileType" className="k-form-label" style={{ width: "30%", marginRight: "10px" }}>
                              File Type
                            </Label>
                            <div className="k-form-field-wrap" style={{ width: "70%" }}>
                              <DropDownList
                                data={fileTypeOptions}
                                defaultValue={fileTypeOptions[0]}
                                id="fileType"
                                textField="key"
                                dataItemKey="key"
                                value={selectedFiletype}
                                onChange={(event) => setSelectedFiletype(event.value)}
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="float-right mr-2">
                          <Button
                            fillMode="solid"
                            themeColor="primary"
                            rounded="full"
                            onClick={() => {
                              setSelectedFolderName(name);
                              handleCreate(fileName, selectedFiletype.value);
                            }}
                          >
                            Create file
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Dialog>
                )}
              </>
            )
          );
        })}
      </CardBody>
    </Card>
  );
};

// /////////////////////////////////////////////////////////////////////
const DocumentView = ({
  core,
  notif,
  selectedDocument,
  setSelectedDocument,
  selectedFolderName,
  onDeleteDocument,
  entityConfig,
  handleShareLink,
  handleRename,
  hasStorageType,
}) => {
  const [renameErrMsg, setRenameErrMsg] = useState("");
  const [currentFileName, setCurrentFileName] = useState("");
  let profileAdapter = core.make("oxzion/profile");

  useEffect(() => {
    if (selectedDocument?.name) {
      setCurrentFileName(removeExtension(selectedDocument.name));
    }
  }, [selectedDocument?.name]);
  const viewUrl = core.config("ui.url");

  const renderFile = (document) => {
    const type = document?.extension;
    const buttons = [
      { type: "edit", icon: "fa-edit", text: "Edit Document" },
      { type: "view", icon: "fa-eye", text: "View Document" },
    ];
    switch (type) {
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return (
          <div className="img-container">
            <img src={document.url} />
          </div>
        );
      case "mp4":
      case "avi":
        return (
          <div className="img-container">
            <video autoplay muted preload controls width="100%">
              <source src={document.url} type={"video/" + type} />
              Sorry, your browser doesn't support embedded videos.
            </video>
          </div>
        );
      case "pdf":
        return (
          <iframe
            src={document.url + "#toolbar=0"}
            // #toolbar=0 hides the toolbar
            className="iframe"
          />
        );
      case "docx":
      case "doc":
      case "xlsx":
      case "xlsm":
      case "xls":
      case "pptx":
      case "ppt":
        if (core.configuration?.storage?.type === "sharepoint") {
        } else if (core.configuration?.storage?.type === "onlyoffice") {
          document.officeViewUrl = viewUrl + "?app=EOXOffice" + "&fileId=" + fileId + "&docId=" + document.uuid;
        }
        setSelectedDocument(document);
        return (
          <div className="my-5 py-5 docviewer-wrapper">
            <h4>Sorry, this file cannot be previewed.</h4>
            <br />
            {hasStorageType && !entityConfig?.disable_create_upload && (
              <div className="action-buttons-container">
                <div className="action-buttons-wrapper">
                  {buttons.map((button) => (
                    <Button key={button.type} className="action-button" onClick={() => handleShareLink(button.type)}>
                      <span className="button-content">
                        <i className={`fa ${button.icon}`} />
                        <span className="button-text">{button.text}</span>
                      </span>
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className="my-5 py-5">
            <h4>Sorry, this file cannot be previewed.</h4>
            <br />
            <p>Please open the downloaded file on your computer using a supported application.</p>
          </div>
        );
    }
  };

  const onChangeName = (e) => {
    const specialCharsRegex = /[.\/\\!@#$%^&*()+=\[\]{};:'"<>?,|]/;
    const newValue = e.target.value;

    if (specialCharsRegex.test(newValue)) {
      setRenameErrMsg("Special characters are not allowed in the file name");
      return;
    }

    setRenameErrMsg("");
    setCurrentFileName(newValue); // Update the input state
  };

  const removeExtension = (docName) => {
    return docName?.substring(0, docName.lastIndexOf(".")) || docName;
  };

  const navigateOrDownload = (e, doc, url) => {
    e.stopPropagation();
    const downloadUrl = (url || doc.url) + "/download";
    window.open(downloadUrl, "_blank").focus();
  };

  return (
    <>
      <div className="mb-2">
        <span>
          <input
            type="text"
            id="filename"
            className={`rename-input pl-2 pt-2 ${renameErrMsg ? "k-invalid" : ""}`}
            value={currentFileName}
            onChange={onChangeName} // Simplified call
            onBlur={(e) => {
              if (renameErrMsg) return; // Don't rename if there's an error
              const oldName = removeExtension(selectedDocument.name);
              const newName = e.target.value.trim();
              if (newName && newName !== oldName) {
                handleRename(newName, oldName, selectedFolderName);
              } else if (!newName) {
                // If input is empty, reset to original name
                setCurrentFileName(oldName);
              }
            }}
            onKeyDown={(e) => {
              // Allow Enter key to trigger rename
              if (e.key === "Enter") {
                e.target.blur(); // This will trigger onBlur
              }
              // Allow Escape key to reset the input
              if (e.key === "Escape") {
                setCurrentFileName(removeExtension(selectedDocument.name));
                setRenameErrMsg("");
                e.target.blur();
              }
            }}
            title={`Rename ${selectedDocument?.originalName}`}
            disabled={(entityConfig?.disable_create_upload)}
          />
          {renameErrMsg && <Error>{renameErrMsg}</Error>}
        </span>
        <div className="float-right">
          {hasStorageType && !entityConfig?.disable_create_upload && (
            <>
              <Button
                className={"ml-1"}
                size={"small"}
                fillMode="clear"
                title="Copy Download URL"
                onClick={() => {
                  notif?.current?.notify("", "Link has been copied to clipboard!", "success", undefined, 3000);
                  core.make("oxzion/link").copyToClipboard(selectedDocument.url);
                }}
              >
                <i className="fa fa-copy"></i>
              </Button>
              <Button
                className={"ml-1"}
                size={"small"}
                fillMode="clear"
                title={`Delete ${selectedDocument?.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteDocument(selectedDocument, selectedFolderName);
                }}
              >
                <i className="fa fa-trash"></i>
              </Button>
            </>
          )}
          <Button
            className={"ml-1"}
            size={"small"}
            fillMode="clear"
            title={`Download ${selectedDocument?.name}`}
            onClick={(e) => navigateOrDownload(e, selectedDocument, selectedDocument.url)}
          >
            <i className="fa fa-arrow-down-to-line"></i>
          </Button>
        </div>
      </div>
      <CreationInfo 
        selectedDocument={selectedDocument}
        core={core}
        profileAdapter={profileAdapter}
      />
      <Card>
        <CardBody className="text-center">{renderFile(selectedDocument)}</CardBody>
      </Card>
    </>
  );
};

export default DocumentViewer;
